'use client';

import type { Ref } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import type { CommandPromptItem } from './command-prompts';
import type { PromptParentTabMode } from './prompt-card-metadata';
import { getPromptCardMeta } from './prompt-card-metadata';

export type PresetPromptCardsProps = {
    prompts: CommandPromptItem[];
    mode: PromptParentTabMode;
    favouriteIds: Set<string>;
    onToggleFavourite: (id: string) => void;
    onSelectPrompt: (prompt: string) => void;
    className?: string;
    loadMoreRef?: Ref<HTMLDivElement>;
    isFetchingMore?: boolean;
    onScrollContainerReady?: (el: HTMLDivElement | null) => void;
};

const cardMotionEase = [0.22, 1, 0.36, 1] as const;

/** Violet top accent without border-t / inset-shadow (avoids hairlines at rounded corners). */
const presetCardSurfaceClass =
    'bg-[linear-gradient(180deg,#7c3aed_0px,#7c3aed_4px,hsl(var(--card))_4px,hsl(var(--card))_100%)]';

/** Category pill: solid dark fill + light text. Shared with prompt library sheet cards. */
export const promptCardCategoryBadgeClass =
    'inline-flex shrink-0 items-center rounded-full border-0 px-2.5 py-1 text-[10px] font-semibold uppercase leading-none tracking-wide shadow-none bg-[#0A0A0A] text-white hover:bg-[#0A0A0A] dark:bg-zinc-800 dark:text-zinc-100 dark:hover:bg-zinc-800';
const SCROLL_STEP_PX = 400;
const EDGE_THRESHOLD = 10;



const presetCardHeightClass = 'h-auto min-h-0';


const presetCardSlideClass =
    'w-full max-sm:w-[75vw] max-sm:min-w-[75vw] shrink-0 self-start min-w-0 sm:w-[290px] sm:max-w-[290px]';

function PresetCardSkeleton() {
    return (
        <div
            className={cn(
                'flex shrink-0 flex-col self-start overflow-hidden rounded-xl border border-border bg-[linear-gradient(180deg,hsl(var(--muted))_0px,hsl(var(--muted))_4px,hsl(var(--card))_4px,hsl(var(--card))_100%)] p-4 pt-3 shadow-sm',
                presetCardSlideClass,
                presetCardHeightClass
            )}
        >
            <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                    <Skeleton className="h-5 w-16 rounded-full bg-zinc-200 dark:bg-zinc-700" />
                    <Skeleton className="h-3 w-20" />
                </div>
                <Skeleton className="h-5 w-full" />
                <Skeleton className="h-5 w-3/4" />
                <div className="rounded-lg bg-muted/40 p-3 space-y-2">
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-3 w-full" />
                    <Skeleton className="h-3 w-full" />
                </div>
                <Skeleton className="h-3 w-32 shrink-0" />
            </div>
        </div>
    );
}

export function PresetPromptCards({
    prompts,
    mode,
    favouriteIds,
    onToggleFavourite,
    onSelectPrompt,
    className,
    loadMoreRef,
    isFetchingMore,
    onScrollContainerReady,
}: PresetPromptCardsProps) {
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const checkScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const { scrollLeft, scrollWidth, clientWidth } = el;
        const overflow = scrollWidth > clientWidth;
        setCanScrollLeft(overflow && scrollLeft > EDGE_THRESHOLD);
        setCanScrollRight(overflow && scrollWidth - clientWidth - scrollLeft > EDGE_THRESHOLD);
    }, []);

    const scrollByDir = useCallback(
        (dir: 'left' | 'right', e?: React.MouseEvent) => {
            e?.preventDefault();
            e?.stopPropagation();
            const el = scrollRef.current;
            if (!el) return;
            const delta = dir === 'left' ? -SCROLL_STEP_PX : SCROLL_STEP_PX;
            el.scrollTo({ left: el.scrollLeft + delta, behavior: 'smooth' });
            requestAnimationFrame(() => checkScroll());
        },
        [checkScroll]
    );

    const setScrollRef = useCallback(
        (node: HTMLDivElement | null) => {
            scrollRef.current = node;
            onScrollContainerReady?.(node);
        },
        [onScrollContainerReady]
    );

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        checkScroll();
        const ro = new ResizeObserver(() => {
            requestAnimationFrame(() => checkScroll());
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [checkScroll, prompts.length, isFetchingMore]);

    const items: Array<{ item?: CommandPromptItem; meta: ReturnType<typeof getPromptCardMeta>; id: string }> =
        prompts.map((item) => ({
            item,
            meta: getPromptCardMeta(item, mode),
            id: item.id,
        }));

    if (items.length === 0) {
        return (
            <motion.div
                className="mx-auto max-w-sm px-3 py-4 text-center max-sm:min-h-0 sm:px-0 sm:py-16"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, ease: cardMotionEase }}
            >
                <p className="whitespace-nowrap text-sm text-muted-foreground max-sm:text-[13px] sm:whitespace-normal">
                    No presets here yet
                </p>
            </motion.div>
        );
    }

    return (
        <div className={cn('relative w-full min-w-0 max-w-full flex-1 min-h-[12rem]', className)}>
            {canScrollLeft && (
                <button
                    type="button"
                    onClick={(e) => scrollByDir('left', e)}
                    className="absolute left-0 top-1/2 z-20 -translate-y-1/2 rounded-full border border-border bg-background/95 p-2 shadow-md backdrop-blur-sm transition-colors hover:bg-muted"
                    aria-label="Scroll presets left"
                >
                    <ChevronLeft className="size-4 text-foreground" />
                </button>
            )}
            {canScrollRight && (
                <button
                    type="button"
                    onClick={(e) => scrollByDir('right', e)}
                    className="absolute right-0 top-1/2 z-20 -translate-y-1/2 rounded-full border border-border bg-background/95 p-2 shadow-md backdrop-blur-sm transition-colors hover:bg-muted"
                    aria-label="Scroll presets right"
                >
                    <ChevronRight className="size-4 text-foreground" />
                </button>
            )}

            <div
                ref={setScrollRef}
                onScroll={() => requestAnimationFrame(() => checkScroll())}
                className={cn(
                    'flex w-full min-w-0 max-w-full flex-nowrap items-start gap-3 overflow-x-auto overflow-y-visible overscroll-x-contain pb-0.5 max-sm:px-1 sm:gap-4 sm:pb-2',
                    '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden',
                    canScrollLeft && 'pl-10',
                    canScrollRight && 'pr-10'
                )}
            >
                {items.map(({ item, meta, id }, index) => {
                    const isFav = item ? favouriteIds.has(item.id) : false;
                    const integrationsLabel = meta.integrations.join(' + ');

                    return (
                        <motion.div
                            key={id}
                            className={presetCardSlideClass}
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                duration: 0.25,
                                delay: index * 0.04,
                                ease: cardMotionEase,
                            }}
                            whileHover={{ y: -2, transition: { type: 'spring', stiffness: 400, damping: 28 } }}
                            whileTap={{ scale: 0.99 }}
                        >
                            <Card
                                className={cn(
                                    'flex h-auto min-h-0 w-full flex-col overflow-hidden rounded-xl border border-border pt-0 shadow-sm transition-shadow',
                                    presetCardSurfaceClass,
                                    presetCardHeightClass,
                                    'cursor-pointer hover:shadow-md'
                                )}
                            >
                                <div
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => {
                                        if (item) onSelectPrompt(item.prompt);
                                    }}
                                    onKeyDown={(e) => {
                                        if (!item) return;
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            onSelectPrompt(item.prompt);
                                        }
                                    }}
                                    className={cn(
                                        'flex min-h-0 min-w-0 flex-col overflow-hidden text-left outline-none',
                                        'cursor-pointer focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0'
                                    )}
                                >
                                    <div className="flex min-h-0 min-w-0 flex-col gap-2 overflow-hidden p-3 pt-2.5 sm:gap-3 sm:p-4 sm:pt-3">
                                        <div className="flex min-w-0 items-center justify-between gap-2">
                                            <div className="flex min-w-0 flex-1 items-center gap-2">
                                                <Badge variant="secondary" className={promptCardCategoryBadgeClass}>
                                                    {meta.badgeLabel}
                                                </Badge>
                                              
                                            </div>
                                            {item && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onToggleFavourite(item.id);
                                                    }}
                                                    className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                                                    aria-label={isFav ? 'Remove from favourites' : 'Add to favourites'}
                                                >
                                                    <Star
                                                        className={cn(
                                                            'size-4',
                                                            isFav && 'fill-amber-400 text-amber-500'
                                                        )}
                                                    />
                                                </button>
                                            )}
                                        </div>

                                        <h3
                                         
                                            className="break-words text-sm font-bold leading-snug text-foreground [overflow-wrap:anywhere] sm:text-[1.05rem]"
                                        >
                                            {meta.subtitle}
                                        </h3>

                                        <div className="min-w-0 shrink-0 rounded-lg bg-violet-50 px-3 py-2 dark:bg-violet-950/40 max-sm:px-2.5 max-sm:py-1.5">
                                            <p className="break-words text-xs leading-relaxed text-foreground [overflow-wrap:anywhere]">
                                                {meta.headline}
                                            </p>
                                        </div>

                                        <p
                                            
                                            className="min-w-0 shrink-0 break-words text-[11px] leading-relaxed text-muted-foreground [overflow-wrap:anywhere]"
                                        >
                                            {integrationsLabel}
                                        </p>
                                    </div>
                                </div>
                            </Card>
                        </motion.div>
                    );
                })}
                {isFetchingMore && (
                    <>
                        <PresetCardSkeleton />
                        <PresetCardSkeleton />
                    </>
                )}
                {loadMoreRef != null && (
                    <div ref={loadMoreRef} className="h-32 w-4 shrink-0 self-center" aria-hidden />
                )}
            </div>
        </div>
    );
}
